import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 15 - light bento, violet accent)
const INK = "#1a1530";
const MUTED = "#5f5a75";
const VIOLET = "#6d28d9";
const BTN_GRADIENT = "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)";
const EASE = [0.22, 1, 0.36, 1];

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": VIOLET,
  "--primary-foreground": "#ffffff",
  "--secondary": "#f3effe",
  "--secondary-foreground": "#4c1d95",
  "--muted": "#f4f3f8",
  "--muted-foreground": MUTED,
  "--accent": "#f3effe",
  "--destructive": "#be123c",
  "--destructive-foreground": "#ffffff",
  "--border": "#e4e2ec",
  "--input": "#b7b1cd",
  "--ring": "#7c3aed",
  "--radius": "0.75rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

function LoginDesign15({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: VIOLET }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: VIOLET }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: VIOLET }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: VIOLET }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  const dist = reduce ? 0 : 16;
  // Standalone entrance props: every element owns its initial/animate (no variant chain)
  const enter = (delay = 0, withOpacity = true) => ({
    initial: withOpacity ? { opacity: 1, y: dist } : { y: dist },
    animate: withOpacity ? { opacity: 1, y: 0 } : { y: 0 },
    transition: { duration: reduce ? 0.15 : 0.5, delay: reduce ? 0 : delay, ease: EASE },
  });

  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld15-company-" + next);
    if (el) el.focus();
  };

  const capsHelper = (
    <div className="ld15-capsslot" aria-live="polite">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            role="status"
            className="ld15-caps"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.05 : 0.18 }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld15-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope
      vars={THEME_VARS}
      className="login15-root ld15-page"
      style={{
        position: "relative",
        width: "100%",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#f4f3f8",
        colorScheme: "light",
      }}
    >
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login15-root, .login15-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        ".ld15-page { min-height: 100vh; }",
        "@supports (min-height: 100dvh) { .ld15-page { min-height: 100dvh; } }",

        ".login15-root .ld15-shell { position: relative; z-index: 1; flex: 1; width: 100%; max-width: 560px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: clamp(20px, 3vw, 32px); min-width: 0; padding: clamp(24px, 5vw, 56px) clamp(16px, 4vw, 32px); }",
        ".login15-root .ld15-brand { min-width: 0; display: flex; flex-direction: column; align-items: center; text-align: center; }",
        ".login15-root .ld15-cardwrap { min-width: 0; width: 100%; max-width: 460px; }",
        ".login15-root .ld15-foot { min-width: 0; margin: 0; font-size: .75rem; color: " + MUTED + "; text-align: center; }",
        ".login15-root .ld15-logo { height: clamp(36px, 4vw, 46px); max-width: 100%; display: block; }",
        ".login15-root .ld15-h2 { margin: 1.1rem 0 .5rem; font-size: clamp(1.6rem, 4.5vw, 2.2rem); font-weight: 800; line-height: 1.15; letter-spacing: -.8px; color: " + INK + "; overflow-wrap: anywhere; }",
        ".login15-root .ld15-h2 span { color: " + VIOLET + "; }",
        ".login15-root .ld15-sub { margin: 0; max-width: 420px; font-size: .95rem; line-height: 1.6; color: " + MUTED + "; }",
        ".login15-root .ld15-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem; margin-top: 1rem; }",
        ".login15-root .ld15-chip { background: #fff; border: 1px solid #ddd6fe; color: #5b21b6; font-size: .72rem; font-weight: 700; }",

        /* Card */
        ".login15-root .ld15-card { position: relative; z-index: 1; overflow: hidden; width: 100%; border: 1px solid #e4e2ec; border-radius: 28px; background: #fff; box-shadow: 0 30px 60px -30px rgba(76,29,149,.38), 0 2px 8px rgba(26,21,48,.06); }",
        ".login15-root .ld15-card::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 4px; background: " + BTN_GRADIENT + "; pointer-events: none; }",
        ".login15-root .ld15-head { padding: clamp(1.4rem, 3vw, 2rem) clamp(1.1rem, 3vw, 2rem) 1.1rem; gap: .9rem; }",
        ".login15-root .ld15-headtop { display: flex; align-items: center; justify-content: space-between; gap: .75rem; }",
        ".login15-root .ld15-badge { width: 48px; height: 48px; border-radius: 15px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; background: " + BTN_GRADIENT + "; box-shadow: 0 10px 20px -8px rgba(91,33,182,.6); }",
        ".login15-root .ld15-title { font-size: clamp(1.45rem, 4vw, 1.75rem); font-weight: 800; letter-spacing: -.5px; line-height: 1.15; color: var(--foreground); overflow-wrap: anywhere; }",
        ".login15-root .ld15-desc { font-size: .88rem; line-height: 1.45; color: var(--muted-foreground); }",
        ".login15-root .ld15-card .ui-card-content { padding: 0 clamp(1.1rem, 3vw, 2rem) 1.1rem; }",
        ".login15-root .ld15-card .ui-card-footer { padding: .9rem clamp(1.1rem, 3vw, 2rem) 1.2rem; justify-content: center; text-align: center; flex-wrap: wrap; font-size: .875rem; color: var(--muted-foreground); }",

        /* Fields */
        ".login15-root .ld15-group { margin-bottom: .25rem; min-width: 0; }",
        ".login15-root .ld15-label { color: var(--foreground); font-weight: 700; font-size: .84rem; margin-bottom: .5rem; line-height: 1.2; }",
        ".login15-root .ld15-labelrow { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: .5rem; flex-wrap: wrap; }",
        ".login15-root .ld15-labelrow .ld15-label { margin-bottom: 0; }",
        ".login15-root .ld15-field { position: relative; min-width: 0; }",
        ".login15-root .ld15-field .ui-input { position: relative; z-index: 0; height: 3.4rem; min-height: 44px; border-radius: 14px; border: 1.5px solid #b7b1cd; background-color: #f6f5fa; color: " + INK + "; -webkit-text-fill-color: " + INK + "; caret-color: " + VIOLET + "; opacity: 1; padding: 0 3rem 0 2.8rem; font-size: 16px; font-weight: 500; text-overflow: ellipsis; }",
        ".login15-root .ld15-field .ld15-pw { padding-right: 5.2rem; }",
        ".login15-root .ld15-field .ui-input::placeholder { color: #6b6580; -webkit-text-fill-color: #6b6580; opacity: 1; }",
        ".login15-root .ld15-field .ui-input:hover { border-color: #8b5cf6; }",
        ".login15-root .ld15-field .ui-input:focus, .login15-root .ld15-field .ui-input:focus-visible { background-color: #fff; color: " + INK + "; -webkit-text-fill-color: " + INK + "; border-color: " + VIOLET + "; box-shadow: 0 0 0 4px rgba(109,40,217,.18); }",
        ".login15-root .ld15-field .ui-input:-webkit-autofill, .login15-root .ld15-field .ui-input:-webkit-autofill:hover, .login15-root .ld15-field .ui-input:-webkit-autofill:focus, .login15-root .ld15-field .ui-input:-webkit-autofill:active { -webkit-text-fill-color: " + INK + " !important; color: " + INK + " !important; caret-color: " + INK + "; -webkit-box-shadow: 0 0 0 100px #f6f5fa inset !important; box-shadow: 0 0 0 100px #f6f5fa inset !important; transition: background-color 9999s ease-out 0s; }",
        ".login15-root .ld15-field .ui-input:autofill { -webkit-text-fill-color: " + INK + "; color: " + INK + "; box-shadow: 0 0 0 100px #f6f5fa inset; }",
        ".login15-root .ld15-fieldicon { position: absolute; z-index: 1; left: .9rem; top: 50%; margin-top: -11px; color: #5f5a75; pointer-events: none; }",
        ".login15-root .ld15-field:focus-within .ld15-fieldicon { color: " + VIOLET + "; }",
        ".login15-root .ld15-capsicon { position: absolute; z-index: 1; right: .95rem; top: 50%; margin-top: -10px; pointer-events: none; }",
        ".login15-root .ld15-capsicon-pw { right: 3.6rem; }",
        ".login15-root .ld15-toggle { position: absolute; z-index: 2; right: .35rem; top: 50%; margin-top: -1.375rem; height: 2.75rem; width: 2.75rem; min-width: 44px; color: #4b4560; border-radius: 12px; }",
        ".login15-root .ld15-toggle:hover { color: #5b21b6; background: rgba(109,40,217,.1); }",
        ".login15-root .ld15-capsslot { min-height: 1.15rem; padding-top: .3rem; }",
        ".login15-root .ld15-caps { display: inline-flex; align-items: center; gap: .35rem; color: #92400e; font-size: 12px; }",
        ".login15-root .ld15-link { display: inline-flex; align-items: center; min-height: 44px; color: #5b21b6; text-decoration: none; font-weight: 700; font-size: .84rem; border-radius: 6px; }",
        ".login15-root .ld15-labelrow .ld15-link { min-height: 32px; }",
        ".login15-root .ld15-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
        ".login15-root .ld15-link:focus-visible { outline: 2px solid " + VIOLET + "; outline-offset: 2px; }",
        ".login15-root .ld15-alert { display: flex; align-items: center; gap: .6rem; background: #fff1f2; border-color: #fecdd3; color: #9f1239; font-weight: 600; font-size: .83rem; margin-bottom: 1rem; border-radius: 12px; overflow-wrap: anywhere; }",

        /* Buttons */
        ".login15-root .ld15-primary { width: 100%; height: 3.3rem; min-height: 44px; border-radius: 14px; font-size: 1rem; font-weight: 800; color: #fff; background: " + BTN_GRADIENT + "; box-shadow: 0 12px 24px -10px rgba(91,33,182,.75); }",
        ".login15-root .ld15-primary:hover { opacity: .95; }",
        ".login15-root .ld15-primary:disabled { opacity: .7; box-shadow: none; }",
        ".login15-root .ld15-bio { width: 100%; height: 3rem; min-height: 44px; margin-top: .75rem; border-radius: 14px; font-weight: 700; color: #5b21b6; border-color: #c4b5fd; }",
        ".login15-root .ld15-bio:hover { background: #f3effe; border-color: " + VIOLET + "; }",

        /* Company cards */
        ".login15-root .ld15-companylist { display: flex; flex-direction: column; gap: .65rem; max-height: min(320px, 45vh); overflow-y: auto; overflow-x: hidden; padding: 4px; margin: 0 -4px 1rem; }",
        ".login15-root .ld15-company { display: flex; align-items: center; gap: .85rem; min-height: 56px; padding: .75rem .9rem; border-radius: 16px; border: 1.5px solid #cfcade; background: #f6f5fa; cursor: pointer; outline: none; box-shadow: none; min-width: 0; }",
        ".login15-root .ld15-company:hover { border-color: #a78bfa; }",
        ".login15-root .ld15-company:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ring); }",
        ".login15-root .ld15-company[aria-checked='true'] { border-color: " + VIOLET + "; background: #f3effe; box-shadow: 0 0 0 4px rgba(109,40,217,.14); }",
        ".login15-root .ld15-avatar { width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1rem; color: #5b21b6; background: #ede9fe; }",
        ".login15-root .ld15-company[aria-checked='true'] .ld15-avatar { color: #fff; background: " + BTN_GRADIENT + "; }",
        ".login15-root .ld15-companyname { flex: 1; min-width: 0; font-weight: 700; font-size: .92rem; color: var(--foreground); overflow-wrap: anywhere; word-break: break-word; }",
        ".login15-root .ld15-radio { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #a99fc7; background: #fff; }",
        ".login15-root .ld15-company[aria-checked='true'] .ld15-radio { border-color: " + VIOLET + "; background: " + VIOLET + "; color: #fff; }",

        /* Session dialog */
        ".login15-root .ld15-signout { min-width: 82px; min-height: 44px; flex-shrink: 0; font-size: .75rem; font-weight: 700; border-radius: 10px; }",
        ".login15-root .ld15-cancel { width: 100%; min-height: 44px; font-weight: 700; color: " + MUTED + "; border-radius: 12px; }",
        ".login15-root .ld15-cancel:hover { background: #f3effe; border-color: #c4b5fd; color: #5b21b6; }",

        "@media (prefers-reduced-motion: reduce) { .login15-root, .login15-root *, .login15-root *::before, .login15-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
      ].join("\n")}</style>

      {/* ── DECORATIVE BACKGROUND (static, below the form) ── */}
      <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(#d3cfe3 1px, transparent 1px)", backgroundSize: "24px 24px", opacity: 0.6, maskImage: "linear-gradient(180deg, #000 0%, transparent 80%)", WebkitMaskImage: "linear-gradient(180deg, #000 0%, transparent 80%)" }} />
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 50% 45% at 0% 0%, rgba(124,58,237,0.10) 0%, transparent 70%), radial-gradient(ellipse 45% 40% at 100% 100%, rgba(167,139,250,0.14) 0%, transparent 70%)" }} />
      </Box>

      {/* ── CONTENT ── */}
      <div className="ld15-shell">
        {/* Brand */}
        <section className="ld15-brand" aria-label="About ReonBook">
          <motion.div {...enter(0)}>
            <img className="ld15-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" />
          </motion.div>
          <motion.h2 className="ld15-h2" {...enter(0.08)}>
            Your books, <span>beautifully organised.</span>
          </motion.h2>
          <motion.p className="ld15-sub" {...enter(0.14)}>
            Sign in to manage invoices, GST and ledgers in one place.
          </motion.p>
          <motion.div className="ld15-chips" {...enter(0.2)}>
            <Badge variant="outline" className="ld15-chip">GST ready</Badge>
            <Badge variant="outline" className="ld15-chip">Multi-company</Badge>
            <Badge variant="outline" className="ld15-chip">Encrypted</Badge>
          </motion.div>
        </section>

        {/* FORM CARD */}
        <div className="ld15-cardwrap">
          <MotionCard
            className="ld15-card"
            initial={{ opacity: 1, y: reduce ? 0 : 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0.15 : 0.7, delay: reduce ? 0 : 0.05, ease: EASE }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div
                  key="login"
                  initial={{ opacity: 1, x: reduce ? 0 : -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: reduce ? 0 : -14 }}
                  transition={{ duration: reduce ? 0.1 : 0.25, ease: EASE }}
                >
                  <CardHeader className="ld15-head">
                    <div className="ld15-headtop">
                      <motion.div {...enter(0.1)} className="ld15-badge" aria-hidden="true">
                        <LockOutlinedIcon sx={{ fontSize: 24 }} />
                      </motion.div>
                      <Badge variant="secondary">Secure sign-in</Badge>
                    </div>
                    <motion.div {...enter(0.16)} style={{ display: "flex", flexDirection: "column", gap: ".35rem", minWidth: 0 }}>
                      <CardTitle role="heading" aria-level={1} className="ld15-title">Welcome</CardTitle>
                      <CardDescription className="ld15-desc">Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div className="ld15-group" {...enter(0.22, false)}>
                        <Label htmlFor="ld15-identifier" className="ld15-label" style={{ display: "block", marginBottom: "0.5rem" }}>
                          Username, Email or Mobile
                        </Label>
                        <div className="ld15-field">
                          <PersonOutlineIcon className="ld15-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld15-identifier"
                            type="text"
                            autoComplete="username"
                            placeholder="Username, email or mobile"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld15-capsicon" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div className="ld15-group" style={{ marginTop: ".6rem" }} {...enter(0.28, false)}>
                        <div className="ld15-labelrow">
                          <Label htmlFor="ld15-password" className="ld15-label">Password</Label>
                          <Link to="/forgot-password" className="ld15-link">Forgot password?</Link>
                        </div>
                        <div className="ld15-field">
                          <LockOutlinedIcon className="ld15-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld15-password"
                            className="ld15-pw"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld15-capsicon ld15-capsicon-pw" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ld15-toggle"
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div style={{ marginTop: ".9rem" }} {...enter(0.34)}>
                        {errorAlert}

                        <MotionButton
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          className="ld15-primary"
                          whileHover={reduce || isLoading ? undefined : { y: -2 }}
                          whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        >
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button
                            type="button"
                            variant="outline"
                            className="ld15-bio"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>
                  </CardContent>

                  <Separator />
                  <CardFooter>
                    <p style={{ margin: 0 }}>
                      Don't have an account?{" "}
                      <Link to="/register" className="ld15-link" style={{ fontSize: "inherit" }}>
                        Sign up now
                      </Link>
                    </p>
                  </CardFooter>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div
                  key="company"
                  initial={{ opacity: 1, x: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: reduce ? 0 : 14 }}
                  transition={{ duration: reduce ? 0.1 : 0.25, ease: EASE }}
                >
                  <CardHeader className="ld15-head">
                    <div className="ld15-headtop">
                      <div className="ld15-badge" aria-hidden="true">
                        <BusinessIcon sx={{ fontSize: 24 }} />
                      </div>
                      <Badge variant="secondary">{companies.length} organization{companies.length !== 1 ? "s" : ""}</Badge>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".35rem", minWidth: 0 }}>
                      <CardTitle role="heading" aria-level={1} className="ld15-title">Select Organization</CardTitle>
                      <CardDescription className="ld15-desc" id="ld15-company-desc">Choose which organization to access</CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div
                      className="ld15-companylist"
                      role="radiogroup"
                      aria-label="Choose your organization"
                      aria-describedby="ld15-company-desc"
                    >
                      {companies.map((c, idx) => {
                        const isSel = selectedCompany?.companyId === c.companyId;
                        const firstFocusable = !selectedCompany && idx === 0;
                        return (
                          <MotionCard
                            key={c.companyId}
                            id={"ld15-company-" + idx}
                            role="radio"
                            aria-checked={isSel}
                            tabIndex={isSel || firstFocusable ? 0 : -1}
                            className="ld15-company"
                            onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                            onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                            initial={{ opacity: 1, y: reduce ? 0 : 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            whileHover={reduce ? undefined : { y: -2 }}
                            whileTap={reduce ? undefined : { scale: 0.99 }}
                            transition={{ duration: reduce ? 0.1 : 0.35, delay: reduce ? 0 : Math.min(idx, 8) * 0.05, ease: EASE }}
                          >
                            <span className="ld15-avatar" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                            <span className="ld15-companyname">{c.companyName}</span>
                            <span className="ld15-radio" aria-hidden="true">
                              <AnimatePresence initial={false}>
                                {isSel && (
                                  <motion.span
                                    key="tick"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    transition={{ duration: reduce ? 0.05 : 0.18 }}
                                    style={{ display: "flex" }}
                                  >
                                    <CheckIcon sx={{ fontSize: 14 }} />
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </span>
                          </MotionCard>
                        );
                      })}
                    </div>

                    <div>
                      {errorAlert}

                      <MotionButton
                        type="button"
                        size="lg"
                        className="ld15-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={reduce ? undefined : { y: -2 }}
                        whileTap={reduce ? undefined : { scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      >
                        Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                      </MotionButton>
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>
        </div>

        {/* Footer */}
        <motion.p className="ld15-foot" initial={{ opacity: 1 }} animate={{ opacity: 1 }} transition={{ delay: reduce ? 0 : 0.7, duration: 0.5 }}>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </motion.p>
      </div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login15-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld15-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(26,21,48,0.5)" } },
          paper: { sx: { borderRadius: "24px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login15-root">
            <Box sx={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)", px: 3, pt: 3, pb: 2.5 }}>
              <Box aria-hidden="true" sx={{ position: "absolute", pointerEvents: "none", width: 150, height: 150, borderRadius: "50%", top: -60, right: -40, border: "1.5px solid rgba(255,255,255,0.2)" }} />
              <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "13px", bgcolor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id="ld15-session-title" component="h2" sx={{ fontWeight: 800, fontSize: "1.08rem", color: "#fff", lineHeight: 1.3 }}>
                    Session Limit Reached
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.9)", mt: 0.2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ position: "relative", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "12px", px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#fff", lineHeight: 1.5 }}>
                  Sign out of one of the devices below to log in on this device.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", mb: 1.5 }}>
                Active Sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <Box key={session.id}
                  component={motion.div}
                  initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: reduce ? 0 : 0.1 + idx * 0.07, ease: EASE }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, mb: 1, borderRadius: "14px",
                    border: "1px solid #d9d6e5",
                    bgcolor: "#f6f5fa",
                    minWidth: 0,
                  }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.87rem", color: INK }}>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: "0.73rem", color: MUTED }}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography sx={{ fontSize: "0.73rem", color: MUTED, ml: 0.5, wordBreak: "break-all" }}>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld15-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, textAlign: "center" }}>
              <Button
                type="button"
                variant="outline"
                className="ld15-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </Button>
            </Box>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign15;
